import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {NewConversationFormValidationSchema} from '../types'
import {useConfirmationDialogStore} from '@/components/ui/confirmation-dialog-atoms/confirmationDialogStore'
import {useCreateConversation} from '../query/useCreateConversation'
import {toast} from '@/utilities/toast/toast'
import {useNavigate} from 'react-router-dom'

interface UseConversationFormOptions {
    onError?: (error: Error) => void
    onSuccess?: () => void
    errorRedirectPath?: string
}

export const useConversationForm = ({onError, onSuccess, errorRedirectPath}: UseConversationFormOptions = {}) => {
    const navigate = useNavigate()

    const selectedMembers = useConfirmationDialogStore(state => state.selectedMembers)

    const conversationForm = useForm<NewConversationFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(NewConversationFormValidationSchema),
        defaultValues: {
            title: '',
            message: '',
            memberIds: [],
            isUrgent: false,
            entityType: undefined,
            entity: undefined
        }
    })

    const createConversationMutation = useCreateConversation({
        onError: error => {
            toast.error(error.message)
            if (errorRedirectPath) {
                navigate(errorRedirectPath)
            }
            onError?.(error)
        },
        onSuccess: () => {
            onSuccess?.()
        }
    })

    const createConversation = async (patientId: string) => {
        const conversationData = conversationForm.getValues()

        if (conversationData.title && conversationData.message && selectedMembers.length > 0) {
            await createConversationMutation.mutateAsync({
                patientId,
                title: conversationData.title,
                message: conversationData.message,
                members: selectedMembers.map(member => ({
                    type: member.type,
                    id: member.id
                })),
                isUrgent: conversationData.isUrgent,
                entityType: conversationData.entityType ?? undefined,
                entityId: conversationData.entity?.id ?? undefined
            })
        }
    }

    return {
        selectedMembers,
        conversationForm,
        createConversationMutation,
        createConversation,
        hasConversationData: () => {
            const data = conversationForm.getValues()
            return !!(data.title && data.message && selectedMembers.length > 0)
        }
    }
}
